import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  Grid,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { ApiService } from '../../services/ApiService';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/user';
import { useAuth } from '../../context/SimpleAuthContext';

interface Requirement {
  id: string;
  title: string;
}

interface TaskCreateData {
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed' | 'blocked';
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignee: string;
  dueDate: string;
  requirementId: string;
}

export default function MaterialTaskCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  
  const [task, setTask] = useState<TaskCreateData>({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    assignee: '',
    dueDate: '',
    requirementId: ''
  });
  
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const canCreate = hasPermission(Permission.TASK_CREATE);

  useEffect(() => {
    fetchRequirements();
  }, []);

  const fetchRequirements = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get<Requirement[]>('/requirements');
      setRequirements(response);
    } catch (error) {
      console.error('Error fetching requirements:', error);
      // Continue without requirements - they're optional
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof TaskCreateData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTask({
      ...task,
      [field]: event.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      // Validate the task
      if (!task.title || !task.description) {
        setError('Title and description are required');
        return;
      }
      
      // Validate due date if provided
      if (task.dueDate && new Date(task.dueDate) < new Date()) {
        setError('Due date must be in the future');
        return;
      }
      
      // Create the task
      await ApiService.post('/tasks', task);
      
      // Redirect to task list
      navigate('/tasks');
      
    } catch (error: any) {
      console.error('Error creating task:', error);
      setError(error.message || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  const getPriorityColor = (priority: TaskCreateData['priority']) => {
    switch (priority) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (!canCreate) {
    return (
      <Box>
        <Alert severity="error">You don't have permission to create tasks</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Create New Task
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                id="task-title-input"
                fullWidth
                label="Task Title"
                value={task.title}
                onChange={handleChange('title')}
                required
                disabled={saving}
                placeholder="Enter a descriptive task title"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="task-description-input"
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={task.description}
                onChange={handleChange('description')}
                required
                disabled={saving}
                placeholder="Provide detailed description of the task"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="task-status-select"
                select
                fullWidth
                label="Initial Status"
                value={task.status}
                onChange={handleChange('status')}
                disabled={saving}
              >
                <MenuItem value="pending">
                  <Chip label="Pending" color="default" size="small" />
                </MenuItem>
                <MenuItem value="in-progress">
                  <Chip label="In Progress" color="primary" size="small" />
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="task-priority-select"
                select
                fullWidth
                label="Priority"
                value={task.priority}
                onChange={handleChange('priority')}
                disabled={saving}
              >
                <MenuItem value="low">
                  <Chip label="Low" color="default" size="small" />
                </MenuItem>
                <MenuItem value="medium">
                  <Chip label="Medium" color="primary" size="small" />
                </MenuItem>
                <MenuItem value="high">
                  <Chip label="High" color="warning" size="small" />
                </MenuItem>
                <MenuItem value="critical">
                  <Chip label="Critical" color="error" size="small" />
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="task-assignee-input"
                fullWidth
                label="Assignee"
                value={task.assignee}
                onChange={handleChange('assignee')}
                disabled={saving}
                placeholder="Enter assignee name"
                helperText="Name of the person responsible for this task"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="task-due-date-input"
                fullWidth
                type="date"
                label="Due Date"
                value={task.dueDate}
                onChange={handleChange('dueDate')}
                disabled={saving}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: new Date().toISOString().split('T')[0]
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="task-requirement-select"
                select
                fullWidth
                label="Related Requirement (Optional)"
                value={task.requirementId}
                onChange={handleChange('requirementId')}
                disabled={saving || loading}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {requirements.map((req) => (
                  <MenuItem key={req.id} value={req.id}>
                    {req.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate('/tasks')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  id="save-task-button"
                >
                  {saving ? 'Creating...' : 'Create Task'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}
